import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useCostPositionsForDepartment } from '@/hooks/finance/useDepartmentCostPositions';
import { usePaymentRequest } from '@/hooks/finance/usePaymentRequests';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  isNew?: boolean;
}

const EditPaymentRequestPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: departments } = useDepartments();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const { data: filteredCategories } = useCostPositionsForDepartment(selectedDepartmentId);
  const { data: request, isLoading } = usePaymentRequest(id || '');

  const [formData, setFormData] = useState({
    department_id: '',
    category_id: '',
    title: '',
    description: '',
    vendor_name: '',
    vendor_bank_name: '',
    vendor_bank_account: '',
    vendor_bank_branch: '',
    payment_method: 'bank_transfer',
    preferred_payment_date: '',
    vat_applicable: false,
  });

  const [items, setItems] = useState<LineItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (request) {
      setFormData({
        department_id: request.department_id || '',
        category_id: request.category_id || '',
        title: request.title,
        description: request.description || '',
        vendor_name: request.vendor_name || '',
        vendor_bank_name: request.vendor_bank_name || '',
        vendor_bank_account: request.vendor_bank_account || '',
        vendor_bank_branch: request.vendor_bank_branch || '',
        payment_method: request.payment_method || 'bank_transfer',
        preferred_payment_date: request.preferred_payment_date || '',
        vat_applicable: request.vat_applicable || false,
      });

      // Set selected department for category filtering
      setSelectedDepartmentId(request.department_id || null);

      if (request.items && request.items.length > 0) {
        setItems(request.items.map(item => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity || 1,
          unit_price: item.unit_price,
          amount: item.amount,
        })));
      } else {
        setItems([{ id: '1', description: '', quantity: 1, unit_price: 0, amount: 0, isNew: true }]);
      }
    }
  }, [request]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // When department changes, update the selected department for filtering and reset category
    if (field === 'department_id') {
      setSelectedDepartmentId(value as string || null);
      setFormData(prev => ({ ...prev, department_id: value as string, category_id: '' }));
    }
  };

  const handleItemChange = (itemId: string, field: keyof LineItem, value: string | number) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          updated.amount = Number(updated.quantity) * Number(updated.unit_price);
        }
        return updated;
      }
      return item;
    }));
  };

  const addItem = () => {
    setItems(prev => [...prev, { 
      id: `new-${Date.now()}`, 
      description: '', 
      quantity: 1, 
      unit_price: 0, 
      amount: 0,
      isNew: true,
    }]);
  };

  const removeItem = (itemId: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const vatAmount = formData.vat_applicable ? subtotal * 0.15 : 0;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const { subtotal, vatAmount, total } = calculateTotals();

  const handleSubmit = async () => {
    if (!id || !formData.title) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Update payment request header
      const { error: requestError } = await supabase
        .from('payment_requests')
        .update({
          ...formData,
          department_id: formData.department_id || null,
          category_id: formData.category_id || null,
          amount: subtotal,
          vat_amount: vatAmount,
          total_amount: total,
        })
        .eq('id', id);
      
      if (requestError) throw requestError;

      // Handle items
      const existingItems = items.filter(i => !i.isNew && i.description);
      const newItems = items.filter(i => i.isNew && i.description);
      const deletedItemIds = request?.items
        ?.filter(originalItem => !items.find(i => i.id === originalItem.id))
        .map(i => i.id) || [];

      // Delete removed items
      if (deletedItemIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('payment_request_items')
          .delete()
          .in('id', deletedItemIds);
        
        if (deleteError) throw deleteError;
      }

      // Update existing items
      for (const item of existingItems) {
        const { error: updateError } = await supabase
          .from('payment_request_items')
          .update({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            amount: item.amount,
          })
          .eq('id', item.id);
        
        if (updateError) throw updateError;
      }

      // Insert new items
      if (newItems.length > 0) {
        const { error: insertError } = await supabase
          .from('payment_request_items')
          .insert(newItems.map(item => ({
            payment_request_id: id,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            amount: item.amount,
          })));
        
        if (insertError) throw insertError;
      }

      queryClient.invalidateQueries({ queryKey: ['payment-requests'] });
      queryClient.invalidateQueries({ queryKey: ['payment-request', id] });
      toast.success('Payment request updated successfully');
      navigate(`/finance/accounting/payment-requests/${id}`);
    } catch (error) {
      console.error('Failed to update payment request:', error);
      toast.error('Failed to update payment request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', { style: 'currency', currency: 'ETB' }).format(amount);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-4xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Payment request not found</h2>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  // Only allow editing draft requests
  if (request.status !== 'draft') {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Cannot edit this request</h2>
          <p className="text-muted-foreground mt-2">Only draft requests can be edited.</p>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Edit Payment Request</h1>
            <p className="text-muted-foreground">{request.request_number}</p>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
              <CardDescription>Basic information about the payment request</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select 
                    value={formData.department_id} 
                    onValueChange={(v) => handleInputChange('department_id', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments?.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(v) => handleInputChange('category_id', v)}
                    disabled={!formData.department_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.department_id ? "Select category" : "Select department first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCategories?.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Brief description of the payment"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Additional Notes</Label>
                <Textarea
                  id="description"
                  placeholder="Any additional information"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Vendor Information */}
          <Card>
            <CardHeader>
              <CardTitle>Vendor / Payee Information</CardTitle>
              <CardDescription>Details about who will receive the payment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vendor_name">Vendor Name</Label>
                <Input
                  id="vendor_name"
                  placeholder="Name of vendor or payee"
                  value={formData.vendor_name}
                  onChange={(e) => handleInputChange('vendor_name', e.target.value)}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_name">Bank Name</Label>
                  <Input
                    id="vendor_bank_name"
                    placeholder="e.g., CBE"
                    value={formData.vendor_bank_name}
                    onChange={(e) => handleInputChange('vendor_bank_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_account">Account Number</Label>
                  <Input
                    id="vendor_bank_account"
                    placeholder="Account number"
                    value={formData.vendor_bank_account}
                    onChange={(e) => handleInputChange('vendor_bank_account', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_branch">Branch</Label>
                  <Input
                    id="vendor_bank_branch"
                    placeholder="Branch name"
                    value={formData.vendor_bank_branch}
                    onChange={(e) => handleInputChange('vendor_bank_branch', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
              <CardDescription>Itemized breakdown of the payment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3 items-start">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity || ''}
                        onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        placeholder="Unit Price"
                        value={item.unit_price || ''}
                        onChange={(e) => handleItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32 flex items-center justify-end font-medium">
                      {formatCurrency(item.amount)}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.vat_applicable}
                      onCheckedChange={(v) => handleInputChange('vat_applicable', v)}
                    />
                    <Label>VAT (15%)</Label>
                  </div>
                  <span className="text-sm">{formatCurrency(vatAmount)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>How and when should this be paid</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Payment Method</Label>
                  <Select 
                    value={formData.payment_method} 
                    onValueChange={(v) => handleInputChange('payment_method', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_payment_date">Preferred Payment Date</Label>
                  <Input
                    id="preferred_payment_date"
                    type="date"
                    value={formData.preferred_payment_date}
                    onChange={(e) => handleInputChange('preferred_payment_date', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditPaymentRequestPage;
